import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { API } from "@/App";
import axios from "axios";
import { Search, Filter, Flame, Clock, Trophy, Users } from "lucide-react";
import Navbar from "@/components/Navbar";
import MatchupCard from "@/components/MatchupCard";

const BrowseDebates = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [debates, setDebates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  useEffect(() => {
    fetchData();
  }, [selectedCategory, selectedStatus]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [debatesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/debates`, {
          params: {
            category: selectedCategory !== "all" ? selectedCategory : undefined,
            status: selectedStatus !== "all" ? selectedStatus : undefined,
            limit: 50
          }
        }),
        axios.get(`${API}/categories`)
      ]);
      setDebates(debatesRes.data);
      setCategories(categoriesRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const filteredDebates = debates.filter(debate => 
    debate.title.toLowerCase().includes(search.toLowerCase()) ||
    debate.description.toLowerCase().includes(search.toLowerCase())
  );

  const liveDebates = filteredDebates.filter(d => d.status === "live");
  const openDebates = filteredDebates.filter(d => d.status === "open");
  const completedDebates = filteredDebates.filter(d => d.status === "completed");

  return (
    <div className="min-h-screen bg-[#0a0a0f] relative">
      {/* Ambient red/blue glow - more visible */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-[20%] -left-[20%] w-[80%] h-[60%] rounded-full bg-red-600/20 blur-[100px]" />
        <div className="absolute -bottom-[20%] -right-[20%] w-[80%] h-[60%] rounded-full bg-blue-600/25 blur-[100px]" />
      </div>
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-12">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-2">
              Browse <span className="text-primary">Debates</span>
            </h1>
            <p className="text-muted-foreground">Watch, vote, or join the battle</p>
          </div>
          
          <Link to="/create">
            <Button 
              data-testid="create-debate-btn"
              className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 font-bold uppercase tracking-wider btn-electric"
            >
              Start a Debate
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-12">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              data-testid="search-debates"
              placeholder="Search debates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-12 bg-zinc-900/50 border-white/10 focus:border-primary"
            />
          </div>
          
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger data-testid="category-filter" className="w-full md:w-48 h-12 bg-zinc-900/50 border-white/10">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.category_id} value={cat.slug}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={selectedStatus} onValueChange={setSelectedStatus}>
            <SelectTrigger data-testid="status-filter" className="w-full md:w-48 h-12 bg-zinc-900/50 border-white/10">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="live">Live Now</SelectItem>
              <SelectItem value="open">Open to Join</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {/* Live Debates */}
            {liveDebates.length > 0 && (
              <section className="mb-16">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse-live"></div>
                  <h2 className="text-2xl font-bold">Live Now</h2>
                  <span className="text-sm text-muted-foreground">({liveDebates.length})</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {liveDebates.map(debate => (
                    <MatchupCard key={debate.debate_id} debate={debate} />
                  ))}
                </div>
              </section>
            )}

            {/* Open Debates */}
            {openDebates.length > 0 && (
              <section className="mb-16">
                <div className="flex items-center gap-3 mb-6">
                  <Clock className="w-5 h-5 text-secondary" />
                  <h2 className="text-2xl font-bold">Open to Join</h2>
                  <span className="text-sm text-muted-foreground">({openDebates.length})</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {openDebates.map(debate => (
                    <MatchupCard key={debate.debate_id} debate={debate} />
                  ))}
                </div>
              </section>
            )}

            {/* Completed Debates */}
            {completedDebates.length > 0 && (
              <section className="mb-16">
                <div className="flex items-center gap-3 mb-6">
                  <Trophy className="w-5 h-5 text-accent" />
                  <h2 className="text-2xl font-bold">Completed</h2>
                  <span className="text-sm text-muted-foreground">({completedDebates.length})</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {completedDebates.map(debate => (
                    <MatchupCard key={debate.debate_id} debate={debate} />
                  ))}
                </div>
              </section>
            )}

            {/* Empty State */}
            {filteredDebates.length === 0 && (
              <div className="text-center py-24">
                <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">No debates found</h3>
                <p className="text-muted-foreground mb-6">Be the first to start a debate in this category!</p>
                <Link to="/create">
                  <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                    Create a Debate
                  </Button>
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BrowseDebates;
